import { castArray, cloneDeep, orderBy } from 'lodash-es';
import { clsx } from 'clsx';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  ElementRef,
  Fragment,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FieldArray, Form, Formik, useField, useFormikContext } from 'formik';

import {
  ConstraintWithRange,
  ConstraintWithNumber as IConstraintWithNumber,
  ConstraintWithStatement as IConstraintWithStatement,
  getEmptyConstraintWithConcepts,
  getEmptyConstraintWithDateRange,
  getEmptyConstraintWithNumber,
  getEmptyConstraintWithOptions,
  getEmptyConstraintWithRange,
  getEmptyConstraintWithStatement,
  getEmptyConstraintWithWave,
  getEmptySegment,
  getEmptySegmentQuestion,
  getEmptyVariableQuota,
  hasConfiguredVariableQuotas,
  isConstraintWithOptions,
  isConstraintWithRanges,
  isConstraintWithStatement,
  MODIFIER_OPTIONS_DEFAULT,
  MODIFIER_OPTION_WITHIN,
  SurveyVariableFormData,
  SurveyVariableSegmentQuestion,
  getInitialSurveyVariableFormData,
  isPopulatedSegment,
  SurveyVariableFormDataValidated,
  validateSurveyVariableData,
} from '../../util/surveyVariables';
import {
  generateQuestionsSection,
  isIdeaPresenterQuestion,
} from '../../util/questions';
import {
  getConceptOptions,
  getOptionOption,
  getOptionOptions,
  getQuestionOption,
} from '../../util/formOptions';
import { getErrorMessage } from 'util/errors';
import { getNestedErrorMessages } from 'util/forms';
import {
  Question,
  QUESTION_TYPE,
  Survey,
  SurveyVariable,
  SurveyVariableModifier,
  SurveyWave,
} from '../../types/domainModels';
import { QuestionGroup } from '../../types/internal';
import { ReactSelectValue } from '../../types/forms';
import { showErrorMessage, showSuccessMessage } from '../../util/notifications';
import {
  useCalculatedValuesSummary,
  useDeleteCalculatedValues,
  useDeleteVariable,
  useSaveSurveyVariables,
  useUploadCalculatedValues,
} from 'hooks/backend/surveyVariables';
import { FeatureFlagName, useHasFeatureFlag } from 'hooks/users';
import { useModal } from '../../hooks/modals';
import { useFieldAutosave, useSubmitValidation } from '../../hooks/forms';
import { useValidateRespondentIds } from 'hooks/backend/respondents';

import AddButton from '../common/forms/AddButton';
import Badge from 'components/common/Badge';
import Button from '../common/forms/Button';
import ButtonLoading from 'components/common/forms/ButtonLoading';
import DeleteVariable from './DeleteVariable';
import DragIcon from '../common/icons/DragIcon';
import DuplicateVariable from './DuplicateVariable';
import ExclamationCircleIcon from '../common/icons/ExclamationCircleIcon';
import ErrorDisplay from '../common/ErrorDisplay';
import FormCheckbox from '../common/forms/FormCheckbox';
import FormDateInput from '../common/forms/FormDateInput';
import FormErrorsAlert from 'components/common/forms/FormErrorsAlert';
import FormInput from '../common/forms/FormInput';
import FormSearchSelectInput from '../common/forms/FormSearchSelectInput';
import Icon from 'components/common/Icon';
import IconBackground from '../common/icons/IconBackground';
import Modal, { ModalHeader } from '../common/Modal';
import Select from '../common/forms/Select';
import SkeletonSurveyCard from './SkeletonSurveyCard';
import SurveyStepStickyHeader from './SurveyStepStickyHeader';
import Textarea from '../common/forms/Textarea';
import UnsavedChangesModal from 'components/common/UnsavedChangesModal';
import VariableQuotasModal from './VariableQuotasModal';
import WordSeparator from '../common/WordSeparator';
import XButton from '../common/forms/XButton';
import { MODIFIER_OPTION_EQUAL_TO } from 'util/displayLogic';
import { getSurveyWaveTitle } from 'util/surveyWaves';
import Tooltip from 'components/common/Tooltip';

interface SurveyVariablesLoadedProps {
  demographicQuestions: Question[];
  initialFormData: SurveyVariableFormData | null;
  isShowingUnsavedChanges: boolean;
  onDirtyChanged(isDirty: boolean): void;
  onDiscardChanges(): void;
  onDismissUnsavedChanges(): void;
  onDuplicate(formData: SurveyVariableFormData): void;
  onHasError(): void;
  onVariableDeleted(): void;
  onVariableSaved(variable: SurveyVariable): void;
  questions: Question[];
  questionsSidebar: ReactNode;
  survey: Survey;
  surveyId: number;
  variable?: SurveyVariable;
  waves: SurveyWave[];
}

const SurveyVariablesPage = ({
  isLoadingDemographicQuestions,
  isLoadingQuestions,
  isLoadingVariables,
  loadVariablesError,
  ...rest
}: SurveyVariablesLoadedProps & {
  isLoadingDemographicQuestions: boolean;
  isLoadingQuestions: boolean;
  isLoadingVariables: boolean;
  loadVariablesError: Error | null;
}) => {
  if (
    isLoadingDemographicQuestions ||
    isLoadingQuestions ||
    isLoadingVariables
  ) {
    return <SkeletonSurveyCard />;
  }

  return loadVariablesError ? (
    <ErrorDisplay
      message={`Failed to load the variable. ${
        loadVariablesError && ` Error: ${loadVariablesError.message}`
      }`}
    />
  ) : (
    <SurveyVariablesLoaded {...rest} />
  );
};

export default SurveyVariablesPage;

const SurveyVariablesLoaded = ({
  demographicQuestions,
  initialFormData,
  isShowingUnsavedChanges,
  onDirtyChanged,
  onDiscardChanges,
  onDismissUnsavedChanges,
  onDuplicate,
  onHasError,
  onVariableDeleted,
  onVariableSaved,
  questions,
  survey,
  surveyId,
  variable,
  waves,
}: SurveyVariablesLoadedProps): JSX.Element => {
  const { isPending: isSavingVariable, mutateAsync: saveVariable } =
    useSaveSurveyVariables({
      onError: (err: Error) => {
        onHasError();
        showErrorMessage({ err });
      },
      onSuccess: (data) => {
        showSuccessMessage('The variable was saved successfully.');
        onVariableSaved(data);
      },
      survey,
      variable,
    });

  const { isPending: isDeletingVariable, mutate: deleteVariable } =
    useDeleteVariable({
      onError: (err) => {
        showErrorMessage({ err });
      },
      onSuccess: () => {
        onVariableDeleted();
      },
    });

  const computedInitialValues = getInitialSurveyVariableFormData({
    questions: [
      ...demographicQuestions,
      ...questions,
      {
        title: 'Use Completion Date',
        id: -1,
        questionTypeId: QUESTION_TYPE.DATE,
      } as Question,
      {
        title: 'Wave',
        id: -2,
        questionTypeId: QUESTION_TYPE.WAVE,
      } as Question,
      {
        title: variable?.title ?? 'Custom Variable',
        id: -3,
        questionTypeId: QUESTION_TYPE.CALCULATED_VARIABLE,
        variableId: variable?.id,
      } as Question,
    ],
    variable,
    waves,
  });

  const initialValues = variable
    ? computedInitialValues
    : (initialFormData ?? computedInitialValues);

  return (
    <Formik<SurveyVariableFormData>
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(formData) => {
        const processedData = formData.isCalculatedVariable
          ? {
              ...formData,
              segments: formData.segments.filter(isPopulatedSegment),
            }
          : formData;
        return saveVariable(processedData as SurveyVariableFormDataValidated);
      }}
      validate={(formData) => {
        return validateSurveyVariableData(formData);
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <Form className="h-full">
        <SurveyVariableForm
          isDeletingVariable={isDeletingVariable}
          isSavingVariable={isSavingVariable}
          isShowingUnsavedChanges={isShowingUnsavedChanges}
          onConfirmDelete={(variableId) => {
            deleteVariable({ surveyId, variableId });
          }}
          onDirtyChanged={onDirtyChanged}
          onDiscardChanges={onDiscardChanges}
          onDismissUnsavedChanges={onDismissUnsavedChanges}
          onDuplicate={onDuplicate}
          onHasError={onHasError}
          questions={questions}
          variable={variable}
          waves={waves}
        />
      </Form>
    </Formik>
  );
};

const SurveyVariableForm = ({
  isDeletingVariable,
  isSavingVariable,
  isShowingUnsavedChanges,
  onConfirmDelete,
  onDirtyChanged,
  onDiscardChanges,
  onDismissUnsavedChanges,
  onDuplicate,
  onHasError,
  questions,
  variable,
  waves,
}: {
  isDeletingVariable: boolean;
  isSavingVariable: boolean;
  isShowingUnsavedChanges: boolean;
  onConfirmDelete(variableId: number): void;
  onDirtyChanged(isDirty: boolean): void;
  onDiscardChanges(): void;
  onDismissUnsavedChanges(): void;
  onDuplicate(formData: SurveyVariableFormData): void;
  onHasError(): void;
  questions: Question[];
  variable: SurveyVariable | undefined;
  waves: SurveyWave[];
}) => {
  const hasCalculatedVariableAccess = useHasFeatureFlag(
    FeatureFlagName.FF_GLA_2833,
  );

  const { dirty, setFieldValue, values } =
    useFormikContext<SurveyVariableFormData>();

  const questionGroups = [
    generateQuestionsSection({
      questions: questions
        .filter((question) => question.isDemographic)
        .map((question) => getQuestionOption({ question })),
      title: 'Demographic Questions',
    }),
    generateQuestionsSection({
      questions: questions
        .filter(
          (question) =>
            ![QUESTION_TYPE.OPEN_ENDED, QUESTION_TYPE.GABOR_GRANGER].includes(
              question.questionTypeId,
            ) && !question.isDemographic,
        )
        .map((question) => {
          return getQuestionOption({ question });
        }),
      title: 'Survey Questions',
    }),
    {
      label: 'DATE RANGE',
      options: [
        {
          label: 'Use Completion Date',
          value: { id: -1, questionTypeId: QUESTION_TYPE.DATE } as Question,
        },
      ],
    },
    {
      label: 'WAVE',
      options: [
        {
          label: 'Wave',
          value: { id: -2, questionTypeId: QUESTION_TYPE.WAVE } as Question,
        },
      ],
    },
    ...(values.isCalculatedVariable && variable?.id
      ? ([
          {
            label: 'CUSTOM VARIABLE',
            options: [
              {
                label: values.title,
                value: {
                  id: -3,
                  questionTypeId: QUESTION_TYPE.CALCULATED_VARIABLE,
                  variableId: variable.id,
                } as Question,
              },
            ],
          },
        ] as QuestionGroup<Question>[])
      : []),
  ];

  useFieldAutosave({
    onValidationError: (errors) => {
      const calcVarError = (errors as { isCalculatedVariable?: string })
        .isCalculatedVariable;
      if (calcVarError) {
        showErrorMessage({ message: calcVarError });
      }
      void setFieldValue('isCalculatedVariable', !values.isCalculatedVariable);
    },
    value: values.isCalculatedVariable,
  });

  const { errors, onClickSubmit, validateAndSubmit } = useSubmitValidation({
    isSaving: isSavingVariable,
    onHasError,
  });

  useEffect(() => {
    onDirtyChanged(dirty);
  }, [dirty, onDirtyChanged]);

  return (
    <div>
      {isShowingUnsavedChanges && (
        <UnsavedChangesModal
          isSaving={isSavingVariable}
          onClickDiscardChanges={onDiscardChanges}
          onClickSaveChanges={validateAndSubmit}
          onCloseModal={onDismissUnsavedChanges}
        />
      )}

      <SurveyStepStickyHeader>
        <h2 className="text-base font-semibold">Variables</h2>

        <ButtonLoading
          disabled={!!(variable && !dirty)}
          hierarchy="primary"
          isLoading={isSavingVariable}
          onClick={onClickSubmit}
          size="sm"
          // This can't currently be a submit button since we handle the form submission
          // in the onClickSubmit callback. If this is a "submit" button, it causes a double submission.
          type="button"
        >
          Save Variable
        </ButtonLoading>
      </SurveyStepStickyHeader>

      <div>
        {errors ? (
          <div className="my-4">
            <FormErrorsAlert errors={getNestedErrorMessages(errors)} />
          </div>
        ) : null}

        <div className="flex justify-between">
          <div className="w-1/2">
            <FormInput label="Title" name="title" size="md" />
          </div>
          {variable && (
            <div className="flex gap-2">
              <DuplicateVariable onDuplicate={onDuplicate} />
              <DeleteVariable
                isDeleting={isDeletingVariable}
                onConfirmDelete={onConfirmDelete}
                variableId={variable?.id}
              />
            </div>
          )}
        </div>

        {hasCalculatedVariableAccess && (
          <div className="mt-2">
            <FormCheckbox
              checkboxLabel="Custom Variable"
              name="isCalculatedVariable"
            />
          </div>
        )}

        {values.isCalculatedVariable && variable && (
          <UploadCalculatedValues variable={variable} waves={waves} />
        )}

        <Segments questions={questionGroups} waves={waves} />
        <hr className="text-light-grey" />
        <Quotas />
        <AddUserIds />
      </div>
    </div>
  );
};

const UploadCalculatedValues = ({
  variable,
  waves,
}: {
  variable: SurveyVariable;
  waves: SurveyWave[];
}): JSX.Element => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadExpanded, setIsUploadExpanded] = useState(false);
  const [isWaveDataExpanded, setIsWaveDataExpanded] = useState(false);
  const [lastUploadedFileName, setLastUploadedFileName] = useState<
    string | null
  >(null);
  const [waveToDelete, setWaveToDelete] = useState<SurveyWave | null>(null);

  const { data: summary, isLoading: isSummaryLoading } =
    useCalculatedValuesSummary({ variableId: variable.id });

  const {
    error: uploadError,
    isError: hasUploadError,
    isPending: isUploading,
    isSuccess: isUploadSuccess,
    mutate: uploadValues,
  } = useUploadCalculatedValues();

  const {
    error: deleteError,
    isError: hasDeleteError,
    isPending: isDeleting,
    mutate: deleteValues,
  } = useDeleteCalculatedValues({
    onSuccess: () => {
      setWaveToDelete(null);
      showSuccessMessage('Wave data cleared successfully.');
    },
    onError: (err) => {
      showErrorMessage({ err });
    },
  });

  return (
    <>
      <div className="mt-4 flex flex-col gap-2">
        <div className="border border-gray-d-200 rounded-lg overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-d-50 text-left"
            onClick={() => setIsUploadExpanded((prev) => !prev)}
            type="button"
          >
            <span className="text-sm font-semibold">Upload Custom Values</span>
            <div
              className={clsx(
                'w-4 h-4 transition-transform',
                isUploadExpanded && 'rotate-180',
              )}
            >
              <Icon id="chevron-down" />
            </div>
          </button>

          {isUploadExpanded && (
            <div className="p-4 bg-gray-d-50 border-t border-gray-d-200">
              <p className="text-sm text-gray-d-500 mb-3">
                Upload a CSV file with two columns: <code>respondentId</code>{' '}
                and <code>customValue</code>. Do not include a header row. Max
                5,000 rows, 5 MB. Existing values will be updated; new rows will
                be added.
              </p>

              {hasUploadError && (
                <div className="mb-3">
                  <ErrorDisplay message={getErrorMessage(uploadError)} />
                </div>
              )}

              {isUploadSuccess && lastUploadedFileName && (
                <div className="flex items-center gap-1.5 mb-3 text-sm text-green-700">
                  <div className="w-4 h-4 flex-shrink-0">
                    <Icon id="check" />
                  </div>
                  <span>
                    <strong>{lastUploadedFileName}</strong> uploaded
                    successfully
                  </span>
                </div>
              )}

              <input
                ref={fileInputRef}
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) {
                    return;
                  }

                  setLastUploadedFileName(file.name);
                  uploadValues({ file, variableId: variable.id });

                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                type="file"
              />

              <ButtonLoading
                hierarchy="secondary-gray"
                isLoading={isUploading}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                size="sm"
                type="button"
              >
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4">
                    <Icon id="upload-01" />
                  </div>
                  Upload CSV
                </div>
              </ButtonLoading>
            </div>
          )}
        </div>

        {waves.length > 0 && (
          <div className="border border-gray-d-200 rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-d-50 text-left"
              onClick={() => setIsWaveDataExpanded((prev) => !prev)}
              type="button"
            >
              <span className="text-sm font-semibold">Wave Data</span>
              <div
                className={clsx(
                  'w-4 h-4 transition-transform',
                  isWaveDataExpanded && 'rotate-180',
                )}
              >
                <Icon id="chevron-down" />
              </div>
            </button>

            {isWaveDataExpanded && (
              <div className="p-4 bg-gray-d-50 border-t border-gray-d-200 flex flex-col gap-1.5">
                {waves.map((wave) => {
                  const waveSummary = summary?.waves.find(
                    (w) => w.waveId === wave.id,
                  );
                  return (
                    <div
                      key={wave.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-d-700">
                          {getSurveyWaveTitle(wave)}
                        </span>
                        {isSummaryLoading ? (
                          <span className="text-xs text-gray-d-400 animate-pulse">
                            —
                          </span>
                        ) : (
                          <span className="text-xs text-gray-d-500">
                            {waveSummary?.total ?? 0} rows
                          </span>
                        )}
                      </div>
                      <Button
                        hierarchy="secondary-destructive"
                        onClick={() => {
                          setWaveToDelete(wave);
                        }}
                        size="sm"
                        type="button"
                      >
                        Clear Data
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {waveToDelete && (
        <Modal
          onCloseModal={() => {
            setWaveToDelete(null);
          }}
        >
          <ModalHeader
            onClickClose={() => {
              setWaveToDelete(null);
            }}
          >
            Clear Wave Data
          </ModalHeader>
          <div className="p-6">
            <p className="text-sm text-gray-d-700 mb-6">
              Are you sure you want to clear all uploaded data for{' '}
              <strong>{getSurveyWaveTitle(waveToDelete)}</strong>? This cannot
              be undone.
            </p>
            {hasDeleteError && (
              <div className="mb-4">
                <ErrorDisplay message={getErrorMessage(deleteError)} />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button
                hierarchy="secondary-gray"
                onClick={() => {
                  setWaveToDelete(null);
                }}
                size="sm"
                type="button"
              >
                Cancel
              </Button>
              <ButtonLoading
                hierarchy="destructive"
                isLoading={isDeleting}
                onClick={() => {
                  deleteValues({
                    variableId: variable.id,
                    waveId: waveToDelete.id,
                  });
                }}
                size="sm"
                type="button"
              >
                Clear Data
              </ButtonLoading>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

const AddUserIds = (): JSX.Element => {
  const {
    isOpen: isAddUsersModalOpen,
    onCloseModal,
    setIsOpen: setIsAddUsersModalOpen,
  } = useModal();

  const [{ value: segments }, , segmentsHelpers] =
    useField<SurveyVariableFormData['segments']>('segments');

  return (
    <div>
      <div className="flex items-center space-x-2">
        <h2>Define Segment by Respondent Ids</h2>
      </div>
      <div className="flex mt-4">
        <AddButton
          label="Specify respondents"
          onClick={() => {
            setIsAddUsersModalOpen(true);
          }}
        />
      </div>

      {isAddUsersModalOpen && (
        <AddUsersEnterScreen
          initialRespondentIdsToAdd={[]}
          onClickAdd={(respondentIdsToAdd) => {
            const newSegments = [
              ...cloneDeep(segments),
              {
                tempId: segments.length.toString(),
                title: '',
                questions: [],
                isUserSpecified: true,
                userIds: respondentIdsToAdd,
              },
            ];

            segmentsHelpers.setValue(newSegments);
            onCloseModal();
          }}
          onCloseModal={onCloseModal}
        />
      )}
    </div>
  );
};

function getDedupedRespondentIds(respondentIds: string) {
  const deduplicatedRespondentIds = new Set(
    respondentIds
      .split('\n')
      .map((id) => {
        return Number(id.trim());
      })
      .filter((id) => !!id),
  );

  return Array.from(deduplicatedRespondentIds);
}

const AddUsersEnterScreen = ({
  initialRespondentIdsToAdd,
  onClickAdd,
  onCloseModal,
}: {
  initialRespondentIdsToAdd: string[];
  onClickAdd(respondentIdsToAdd: number[]): void;
  onCloseModal(): void;
}): JSX.Element => {
  const respondentsField = useRef<ElementRef<'textarea'>>(null);
  const [respondentIdsToAdd, setRespondentIdsToAdd] = useState(
    initialRespondentIdsToAdd.join('\n'),
  );

  const {
    error: validateRespondentIdsError,
    isError: hasValidateRespondentIdsError,
    isPending: isValidatingRespondentIds,
    mutate: validateRespondentIds,
  } = useValidateRespondentIds({
    onSuccess: () => {
      onClickAdd(getDedupedRespondentIds(respondentIdsToAdd));
    },
  });

  return (
    <Modal
      header={
        <ModalHeader onClickClose={onCloseModal}>Add Respondents</ModalHeader>
      }
      initialFocus={respondentsField}
      onCloseModal={onCloseModal}
      position="top"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // Stopping propagation here prevents a "parent" form from being submitted. This dialog
          // is currently on the survey variables creation page so we don't want to submit that
          // form.
          event.stopPropagation();

          validateRespondentIds({
            data: {
              respondentIds: getDedupedRespondentIds(respondentIdsToAdd),
            },
          });
        }}
      >
        {hasValidateRespondentIdsError ? (
          <div className="mb-4">
            <ErrorDisplay
              message={getErrorMessage(validateRespondentIdsError)}
            />
          </div>
        ) : null}

        <p className="mb-2 text-sm">
          Use the below field to paste respondent IDs to add to variable
          segment. Each respondent ID should be on its own line.
        </p>
        <Textarea
          ref={respondentsField}
          onChange={(event) => {
            setRespondentIdsToAdd(event.target.value);
          }}
          rows={15}
          size="lg"
          value={respondentIdsToAdd}
        />
        <div className="flex flex-row-reverse justify-end mt-8 gap-3">
          <ButtonLoading
            disabled={!respondentIdsToAdd}
            grow
            hierarchy="primary"
            isLoading={isValidatingRespondentIds}
            size="lg"
            type="submit"
          >
            Add
          </ButtonLoading>
          <Button
            grow
            hierarchy="secondary-gray"
            onClick={onCloseModal}
            size="lg"
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const Segments = ({
  questions,
  waves,
}: {
  questions: QuestionGroup<Question>[];
  waves: SurveyWave[];
}) => {
  const [{ value: segments }] =
    useField<SurveyVariableFormData['segments']>('segments');

  return (
    <div className="py-6">
      <h2 className="mb-2">Segments</h2>
      <FieldArray
        name="segments"
        render={(arrayHelpers) => {
          return (
            <>
              <DragDropContext
                onDragEnd={({ destination, source }) => {
                  if (!destination || source.index === destination.index) {
                    return;
                  }

                  arrayHelpers.move(source.index, destination.index);
                }}
              >
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {segments.map((_segment, index) => {
                        return (
                          <DraggableSegment
                            key={index}
                            arrayHelpers={arrayHelpers}
                            index={index}
                            questions={questions}
                            waves={waves}
                          />
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              <div className="flex mt-4">
                <AddButton
                  label="Add Segment"
                  onClick={() => {
                    arrayHelpers.push(
                      getEmptySegment({ numExistingSegments: segments.length }),
                    );
                  }}
                />
              </div>
            </>
          );
        }}
      />
    </div>
  );
};

const DraggableSegment = ({
  arrayHelpers,
  index,
  questions,
  waves,
}: {
  arrayHelpers: {
    push(value: SurveyVariableFormData['segments'][number]): void;
    remove(idx: number): void;
  };
  index: number;
  questions: QuestionGroup<Question>[];
  waves: SurveyWave[];
}) => {
  const [isHovering, setIsHovering] = useState(false);

  const [{ value: segments }] =
    useField<SurveyVariableFormData['segments']>('segments');

  return (
    <Draggable draggableId={`option-${index}`} index={index}>
      {(provided, snapshot) => {
        const isDragIconVisible = isHovering || snapshot.isDragging;

        return (
          <div
            ref={provided.innerRef}
            className="relative"
            {...provided.draggableProps}
            onMouseEnter={() => {
              setIsHovering(true);
            }}
            onMouseLeave={() => {
              setIsHovering(false);
            }}
          >
            <div
              {...provided.dragHandleProps}
              className="flex absolute top-1/2 left-0 flex-col items-center justify-center transform -translate-y-1/2 -translate-x-full h-full"
              tabIndex={-1}
            >
              <div
                className={clsx('w-4 h-4 text-dark-grey', {
                  visible: isDragIconVisible,
                  invisible: !isDragIconVisible,
                })}
              >
                <DragIcon />
              </div>
            </div>
            <div key={index} className="py-4 border-b border-light-grey">
              <Segment
                index={index}
                onClickDuplicate={() => {
                  const segmentToDuplicate = cloneDeep(segments[index]);
                  delete segmentToDuplicate.id;

                  arrayHelpers.push({
                    ...segmentToDuplicate,
                    tempId: `${segments.length + 1}`,
                    title: `Copy of ${segmentToDuplicate.title}`,
                  });
                }}
                onClickRemove={() => {
                  arrayHelpers.remove(index);
                }}
                questions={questions}
                waves={waves}
              />
            </div>
          </div>
        );
      }}
    </Draggable>
  );
};

const Segment = ({
  index,
  onClickDuplicate,
  onClickRemove,
  questions,
  waves,
}: {
  index: number;
  onClickDuplicate(): void;
  onClickRemove(): void;
  questions: QuestionGroup<Question>[];
  waves: SurveyWave[];
}) => {
  const segmentOrGroupsFieldName = `segments.${index}.questions`;
  const userIdsFieldName = `segments.${index}.isUserSpecified`;

  const [{ value: segmentOrGroups }] = useField<
    SurveyVariableFormData['segments'][number]['questions']
  >(segmentOrGroupsFieldName);
  const [{ value: isUserSpecified }] =
    useField<SurveyVariableFormData['segments'][number]['isUserSpecified']>(
      userIdsFieldName,
    );

  return (
    <div className="bg-gray-d-50 border border-gray-d-200 rounded-lg p-4">
      <div className="flex items-center space-x-3">
        <div className="w-80">
          <FormInput
            name={`segments.${index}.title`}
            placeholder="Segment title..."
            size="md"
          />
        </div>
        <IconBackground onClick={onClickDuplicate} title="Duplicate">
          <div className="w-3 h-3">
            <Icon id="copy-02" />
          </div>
        </IconBackground>
        <IconBackground onClick={onClickRemove} title="Remove">
          <div className="w-3 h-3">
            <Icon id="trash" />
          </div>
        </IconBackground>
        {isUserSpecified && (
          <Badge color="primary" size="sm">
            Specified respondents
          </Badge>
        )}
      </div>
      {!isUserSpecified && (
        <div className="pl-4 w-full mt-4">
          <FieldArray
            name={segmentOrGroupsFieldName}
            render={(orGroupHelpers) => {
              return (
                <>
                  {segmentOrGroups.map((_orGroup, index) => {
                    return (
                      <Fragment key={index}>
                        <div className="bg-white border border-orange-d-200 rounded-md p-3 shadow-sm">
                          <div className="relative">
                            <SurveyVariableOrGroup
                              fieldName={`${segmentOrGroupsFieldName}.${index}`}
                              onRemoveGroup={() => {
                                if (segmentOrGroups.length === 1) {
                                  orGroupHelpers.replace(0, [
                                    getEmptySegmentQuestion(),
                                  ]);
                                } else {
                                  orGroupHelpers.remove(index);
                                }
                              }}
                              questions={questions}
                              waves={waves}
                            />
                          </div>
                        </div>
                        <WordSeparator
                          variant={
                            index !== segmentOrGroups.length - 1
                              ? 'or'
                              : 'default'
                          }
                          word={
                            index !== segmentOrGroups.length - 1
                              ? 'or'
                              : undefined
                          }
                        />
                      </Fragment>
                    );
                  })}
                  {
                    <div className="flex">
                      <AddButton
                        label="or"
                        onClick={() => {
                          orGroupHelpers.push([getEmptySegmentQuestion()]);
                        }}
                      />
                    </div>
                  }
                </>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

const SurveyVariableOrGroup = ({
  fieldName,
  onRemoveGroup,
  questions,
  waves,
}: {
  fieldName: string;
  onRemoveGroup(): void;
  questions: QuestionGroup<Question>[];
  waves: SurveyWave[];
}) => {
  const [{ value: segmentQuestions }] =
    useField<SurveyVariableFormData['segments'][number]['questions'][number]>(
      fieldName,
    );

  return (
    <FieldArray
      name={fieldName}
      render={(arrayHelpers) => {
        return (
          <>
            <div className="space-y-3">
              {segmentQuestions.map((_segmentQuestion, i) => {
                return (
                  <Fragment key={i}>
                    <SegmentQuestion
                      fieldName={`${fieldName}.${i}`}
                      onClickRemove={() => {
                        arrayHelpers.remove(i);

                        if (segmentQuestions.length === 1) {
                          onRemoveGroup();
                        }
                      }}
                      questions={questions}
                      waves={waves}
                    />
                    {i !== segmentQuestions.length - 1 && (
                      <WordSeparator variant="and" word="and" />
                    )}
                  </Fragment>
                );
              })}
            </div>
            <div className="flex mt-3 justify-between">
              <AddButton
                label="and"
                onClick={() => {
                  arrayHelpers.push(getEmptySegmentQuestion());
                }}
              />
              {segmentQuestions.length === 1 && (
                <Tooltip
                  trigger={
                    <Button
                      hierarchy="secondary-gray"
                      onClick={() => {
                        const segmentQuestion = segmentQuestions[0];
                        const questionValue = segmentQuestion.question?.value;

                        if (!questionValue) {
                          return;
                        }

                        const selectedOptionIds = new Set<number>();
                        segmentQuestion.constraints.forEach((constraint) => {
                          if (isConstraintWithStatement(constraint)) {
                            if (constraint.statement) {
                              selectedOptionIds.add(
                                constraint.statement.value.id,
                              );
                            }
                          } else if (isConstraintWithRanges(constraint)) {
                            if (constraint.option) {
                              selectedOptionIds.add(constraint.option.value.id);
                            }
                          } else if (isConstraintWithOptions(constraint)) {
                            castArray(constraint.options).forEach((option) => {
                              selectedOptionIds.add(option.value.id);
                            });
                          }
                        });

                        const remainingOptions = questionValue.options.filter(
                          (option) => !selectedOptionIds.has(option.id),
                        );

                        remainingOptions.forEach((option) => {
                          const newSegmentQuestion = cloneDeep(segmentQuestion);

                          newSegmentQuestion.constraints =
                            newSegmentQuestion.constraints.map((constraint) => {
                              if (isConstraintWithStatement(constraint)) {
                                return {
                                  ...constraint,
                                  statement: getOptionOption({ option }),
                                };
                              }

                              if (isConstraintWithRanges(constraint)) {
                                return {
                                  ...constraint,
                                  option: getOptionOption({ option }),
                                };
                              }

                              if (isConstraintWithOptions(constraint)) {
                                return {
                                  ...constraint,
                                  options: getOptionOption({ option }),
                                };
                              }

                              return constraint;
                            });

                          arrayHelpers.push(newSegmentQuestion);
                        });
                      }}
                      size="sm"
                    >
                      Duplicate across question options
                    </Button>
                  }
                >
                  When clicked, this button will copy the question, modifier,
                  and selections and create new segments with the same choices
                  for each different question option.
                </Tooltip>
              )}
            </div>
          </>
        );
      }}
    />
  );
};

const SegmentQuestion = ({
  fieldName,
  onClickRemove,
  questions,
  waves,
}: {
  fieldName: string;
  onClickRemove(): void;
  questions: QuestionGroup<Question>[];
  waves: SurveyWave[];
}) => {
  const modifierFieldName = `${fieldName}.modifier`;
  const constraintsFieldName = `${fieldName}.constraints`;

  const [{ value: question }] = useField<
    SurveyVariableSegmentQuestion['question']
  >(`${fieldName}.question`);
  const [{ value: logicalModifier }, , logicalModifierHelpers] =
    useField<SurveyVariableSegmentQuestion['modifier']>(modifierFieldName);
  const [, , constraintsHelpers] =
    useField<SurveyVariableSegmentQuestion['constraints']>(
      constraintsFieldName,
    );

  const isNumberQuestion =
    question?.value.questionTypeId === QUESTION_TYPE.NUMBER ||
    (question?.value.questionTypeId === QUESTION_TYPE.OPEN_ENDED &&
      question?.value.questionFeatures?.find(
        (f) => f.feature.code === 'Number',
      ));

  let constraintsContent: ReactNode = null;
  if (!question) {
    // This empty select is just decoration before a user chooses a question. It looks
    // better to have this than an empty space.
    constraintsContent = (
      <Select
        onChange={() => {
          // pass
        }}
        options={[]}
        value={null}
      />
    );
  } else if (isIdeaPresenterQuestion(question.value)) {
    constraintsContent = (
      <ConstraintWithConcepts
        logicalModifier={logicalModifier?.value}
        namePrefix={constraintsFieldName}
        question={question.value}
      />
    );
  } else if (question.value.questionTypeId === QUESTION_TYPE.MULTIPLE_CHOICE) {
    constraintsContent = (
      <ConstraintWithOptions
        logicalModifier={logicalModifier?.value}
        namePrefix={constraintsFieldName}
        question={question.value}
      />
    );
  } else if (
    question.value.questionTypeId === QUESTION_TYPE.ALLOCATION ||
    question.value.questionTypeId === QUESTION_TYPE.SCALE
  ) {
    constraintsContent = (
      <ConstraintWithRanges
        logicalModifier={logicalModifier?.value}
        namePrefix={constraintsFieldName}
        optionRangeSeparator={
          <span className="text-xs">
            with a value
            <br />
            between
          </span>
        }
        question={question.value}
      />
    );
  } else if (question.value.questionTypeId === QUESTION_TYPE.RANKING) {
    constraintsContent = (
      <ConstraintWithRanges
        logicalModifier={logicalModifier?.value}
        namePrefix={constraintsFieldName}
        optionRangeSeparator={
          <span className="text-xs">
            with a rank
            <br />
            value between
          </span>
        }
        question={question.value}
      />
    );
  } else if (question.value.questionTypeId === QUESTION_TYPE.MATRIX) {
    constraintsContent = (
      <ConstraintWithStatement
        logicalModifier={logicalModifier?.value}
        namePrefix={constraintsFieldName}
        question={question.value}
      />
    );
  } else if (question.value.questionTypeId === QUESTION_TYPE.DATE) {
    constraintsContent = (
      <ConstraintWithDateRange namePrefix={constraintsFieldName} />
    );
  } else if (question.value.questionTypeId === QUESTION_TYPE.WAVE) {
    constraintsContent = (
      <ConstraintWithWave namePrefix={constraintsFieldName} waves={waves} />
    );
  } else if (
    question.value.questionTypeId === QUESTION_TYPE.CALCULATED_VARIABLE
  ) {
    constraintsContent = (
      <ConstraintWithNumber namePrefix={constraintsFieldName} />
    );
  } else if (isNumberQuestion) {
    constraintsContent = (
      <ConstraintWithNumber namePrefix={constraintsFieldName} />
    );
  }

  let filteredModifiers = MODIFIER_OPTIONS_DEFAULT;
  if (
    isNumberQuestion ||
    question?.value.questionTypeId === QUESTION_TYPE.DATE ||
    question?.value.questionTypeId === QUESTION_TYPE.CALCULATED_VARIABLE
  ) {
    filteredModifiers = [MODIFIER_OPTION_WITHIN];
  }

  if (question?.value.questionTypeId === QUESTION_TYPE.WAVE) {
    filteredModifiers = [MODIFIER_OPTION_EQUAL_TO];
  }

  return (
    <div className="flex w-full space-x-4 bg-primary-d-50 border border-primary-d-200 rounded-md p-3">
      <div className="flex-shrink-0 w-72">
        <FormSearchSelectInput<ReactSelectValue<Question>>
          name={`${fieldName}.question`}
          onChange={(newQuestion) => {
            if (!newQuestion || newQuestion.value.id === question?.value.id) {
              return;
            }

            constraintsHelpers.setError('');

            if (
              newQuestion.value.questionTypeId === QUESTION_TYPE.ALLOCATION ||
              newQuestion.value.questionTypeId === QUESTION_TYPE.RANKING ||
              newQuestion.value.questionTypeId === QUESTION_TYPE.SCALE
            ) {
              constraintsHelpers.setValue([getEmptyConstraintWithRange()]);
            } else if (
              newQuestion.value.questionTypeId === QUESTION_TYPE.MATRIX
            ) {
              constraintsHelpers.setValue([getEmptyConstraintWithStatement()]);
            } else if (
              newQuestion.value.questionTypeId === QUESTION_TYPE.NUMBER
            ) {
              logicalModifierHelpers.setValue(MODIFIER_OPTION_WITHIN);
              constraintsHelpers.setValue([getEmptyConstraintWithNumber()]);
            } else if (
              newQuestion.value.questionTypeId === QUESTION_TYPE.DATE
            ) {
              logicalModifierHelpers.setValue(MODIFIER_OPTION_WITHIN);
              constraintsHelpers.setValue([getEmptyConstraintWithDateRange()]);
            } else if (
              newQuestion.value.questionTypeId === QUESTION_TYPE.WAVE
            ) {
              constraintsHelpers.setValue([getEmptyConstraintWithWave()]);
            } else if (
              newQuestion.value.questionTypeId ===
              QUESTION_TYPE.CALCULATED_VARIABLE
            ) {
              logicalModifierHelpers.setValue(MODIFIER_OPTION_WITHIN);
              constraintsHelpers.setValue([getEmptyConstraintWithNumber()]);
            } else if (isIdeaPresenterQuestion(newQuestion.value)) {
              constraintsHelpers.setValue([getEmptyConstraintWithConcepts()]);
            } else {
              constraintsHelpers.setValue([getEmptyConstraintWithOptions()]);
            }
          }}
          options={questions}
          placeholder="Question..."
        />
      </div>
      <span className="flex-shrink-0 mt-2 text-xs font-medium text-primary-d-700">
        is
      </span>
      <div className="flex-shrink-0 w-24">
        <FormSearchSelectInput
          // Number questions currently one have one option for the modifier (which is set during
          // selection of the number question).
          isDisabled={
            !!isNumberQuestion ||
            question?.value.questionTypeId === QUESTION_TYPE.DATE ||
            question?.value.questionTypeId === QUESTION_TYPE.CALCULATED_VARIABLE
          }
          name={`${fieldName}.modifier`}
          options={filteredModifiers}
        />
      </div>
      {/*
        Constraints need some width (hence the w-px) or else they'll grow without bound if the option
        titles are long.
      */}
      <div className="flex-grow w-px">{constraintsContent}</div>
      <div className="mt-2">
        <IconBackground onClick={onClickRemove} size="small" title="Remove">
          <div className="w-3 h-3">
            <Icon id="x" />
          </div>
        </IconBackground>
      </div>
    </div>
  );
};

const ConstraintWithStatement = ({
  logicalModifier,
  namePrefix,
  question,
}: {
  logicalModifier: SurveyVariableModifier | undefined;
  namePrefix: string;
  question: Question;
}) => {
  const [{ value: constraints }] =
    useField<IConstraintWithStatement[]>(namePrefix);

  const options = orderBy(question.matrixOptions, (o) => o.sort).map(
    (option) => {
      return {
        label: option.title,
        value: option,
      };
    },
  );

  return (
    <FieldArray
      name={namePrefix}
      render={(arrayHelpers) => {
        return (
          <div>
            <div className="space-y-2">
              {constraints.map((_constraintWithStatement, index) => {
                const constraintFieldName = `${namePrefix}.${index}`;

                return (
                  <div key={index} className="flex space-x-2">
                    <div className="w-1/2">
                      <FormSearchSelectInput
                        name={`${constraintFieldName}.statement`}
                        options={getOptionOptions({
                          options: question.options,
                        })}
                      />
                    </div>
                    <div className="w-1/2">
                      <FormSearchSelectInput
                        isMulti={logicalModifier !== 'is'}
                        name={`${constraintFieldName}.options`}
                        options={options}
                      />
                    </div>
                    {constraints.length > 1 && (
                      <div className="mt-2">
                        <XButton
                          onClick={() => {
                            arrayHelpers.remove(index);
                          }}
                          title="Remove"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex mt-2">
              <AddButton
                label={logicalModifier === 'is' ? 'and' : 'or'}
                onClick={() => {
                  arrayHelpers.push(getEmptyConstraintWithStatement());
                }}
              />
            </div>
          </div>
        );
      }}
    />
  );
};

const ConstraintWithConcepts = ({
  logicalModifier,
  namePrefix,
  question,
}: {
  logicalModifier: SurveyVariableModifier | undefined;
  namePrefix: string;
  question: Question;
}): JSX.Element => {
  return (
    <FormSearchSelectInput
      isMulti={logicalModifier !== 'is'}
      name={`${namePrefix}.0.concepts`}
      options={getConceptOptions({ concepts: question.concepts ?? [] })}
    />
  );
};

const ConstraintWithOptions = ({
  logicalModifier,
  namePrefix,
  question,
}: {
  logicalModifier: SurveyVariableModifier | undefined;
  namePrefix: string;
  question: Question;
}): JSX.Element => {
  return (
    <FormSearchSelectInput
      isMulti={logicalModifier !== 'is'}
      name={`${namePrefix}.0.options`}
      options={getOptionOptions({ options: question.options })}
    />
  );
};

const ConstraintWithDateRange = ({
  namePrefix,
}: {
  namePrefix: string;
}): JSX.Element => {
  return (
    <div className="flex space-x-2">
      <FormDateInput name={`${namePrefix}[0].dateRange.start`} />
      <span className="flex-shrink-0 mt-2 text-xs">and</span>
      <FormDateInput name={`${namePrefix}[0].dateRange.end`} />
    </div>
  );
};

const ConstraintWithWave = ({
  namePrefix,
  waves,
}: {
  namePrefix: string;
  waves: SurveyWave[];
}): JSX.Element => {
  const waveOptions = waves.map((wave) => ({
    label: getSurveyWaveTitle(wave),
    value: wave,
  }));

  return (
    <FormSearchSelectInput
      name={`${namePrefix}.0.waves`}
      options={waveOptions}
    />
  );
};

const ConstraintWithRanges = ({
  logicalModifier,
  namePrefix,
  optionRangeSeparator,
  question,
}: {
  logicalModifier: SurveyVariableModifier | undefined;
  namePrefix: string;
  optionRangeSeparator?: ReactNode;
  question: Question;
}): JSX.Element => {
  const [{ value: constraints }] = useField<ConstraintWithRange[]>(namePrefix);

  return (
    <FieldArray
      name={namePrefix}
      render={(arrayHelpers) => {
        return (
          <div>
            <div className="space-y-2">
              {constraints.map((_range, index) => {
                const constraintFieldName = `${namePrefix}.${index}`;

                return (
                  <div key={index} className="flex space-x-2">
                    <div className="flex-grow">
                      <FormSearchSelectInput
                        name={`${constraintFieldName}.option`}
                        options={getOptionOptions({
                          options: question.options,
                        })}
                      />
                    </div>
                    {optionRangeSeparator}
                    <div className="flex space-x-2">
                      <div className="w-16">
                        <FormInput
                          name={`${constraintFieldName}.range.start`}
                          size="md"
                          type="number"
                        />
                      </div>
                      <span className="mt-2 text-xs">and</span>
                      <div className="w-16">
                        <FormInput
                          name={`${constraintFieldName}.range.end`}
                          size="md"
                          type="number"
                        />
                      </div>
                    </div>
                    {constraints.length > 1 && (
                      <div className="mt-2">
                        <XButton
                          onClick={() => {
                            arrayHelpers.remove(index);
                          }}
                          title="Remove"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex mt-2">
              <AddButton
                label={logicalModifier === 'is' ? 'and' : 'or'}
                onClick={() => {
                  arrayHelpers.push(getEmptyConstraintWithRange());
                }}
              />
            </div>
          </div>
        );
      }}
    />
  );
};

const ConstraintWithNumber = ({
  namePrefix,
}: {
  namePrefix: string;
}): JSX.Element => {
  const [{ value: constraints }] =
    useField<IConstraintWithNumber[]>(namePrefix);

  return (
    <FieldArray
      name={namePrefix}
      render={(arrayHelpers) => {
        return (
          <div>
            <div className="space-y-2">
              {constraints.map((_range, index) => {
                const constraintFieldName = `${namePrefix}.${index}`;

                return (
                  <div key={index} className="flex space-x-2">
                    <div className="flex-grow">
                      <FormInput
                        name={`${constraintFieldName}.range.start`}
                        size="md"
                        type="number"
                      />
                    </div>
                    <span className="mt-2 text-xs">and</span>
                    <div className="flex-grow">
                      <FormInput
                        name={`${constraintFieldName}.range.end`}
                        size="md"
                        type="number"
                      />
                    </div>
                    {constraints.length > 1 && (
                      <div className="mt-2">
                        <XButton
                          onClick={() => {
                            arrayHelpers.remove(index);
                          }}
                          title="Remove"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }}
    />
  );
};

const Quotas = (): JSX.Element => {
  const [{ value: quotas }, quotasMeta, quotasHelpers] =
    useField<SurveyVariableFormData['quotas']>('quotas');

  const {
    isOpen: isQuotasModalOpen,
    onCloseModal,
    setIsOpen: setIsQuotasModalOpen,
  } = useModal();

  const hasQuotas = hasConfiguredVariableQuotas(quotas);
  const hasQuotasError = !!quotasMeta.error;

  function openQuotasModal() {
    if (quotas.length === 0) {
      quotasHelpers.setValue([getEmptyVariableQuota()]);
    }

    setIsQuotasModalOpen(true);
  }

  return (
    <div className="py-6">
      <div className="flex items-center space-x-2">
        <h2
          className={clsx({
            'text-red font-bold': hasQuotasError,
          })}
        >
          Quotas
        </h2>
        {hasQuotasError && (
          <div className="w-5 h-5 text-red">
            <ExclamationCircleIcon />
          </div>
        )}
      </div>

      {hasQuotasError && (
        <p className="mt-2 text-sm text-red">
          The quotas are misconfigured. Please adjust or remove.
        </p>
      )}

      {hasQuotas && (
        <div className="mt-2 pl-4">
          <QuotasDisplay quotas={quotas} />
        </div>
      )}

      <div className="flex mt-4">
        <AddButton
          label="Manage Quotas"
          onClick={() => {
            openQuotasModal();
          }}
        />
      </div>

      {isQuotasModalOpen && <VariableQuotasModal onCloseModal={onCloseModal} />}
    </div>
  );
};

const QuotasDisplay = ({
  quotas,
}: {
  quotas: SurveyVariableFormData['quotas'];
}): JSX.Element => {
  return (
    <ul className="list-disc pl-8 text-sm">
      {quotas.map((quota, i) => {
        const modifierValue = quota.type?.value;

        return (
          <li key={i}>
            <strong className="font-semibold">{quota.type?.label}</strong>{' '}
            {modifierValue === 'at_most' ? quota.value : ''} responses for
            segments:
            <ul className="list-circle pl-8">
              {quota.segments.map((segment, i) => {
                return <li key={i}>{segment.label}</li>;
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
};
